import { useEffect, useState } from "react";
import { Plus, Trash2, Handshake, Gift } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Page, Spinner } from "@/components/shared";

const CATEGORIES = ["ride", "hydration", "charging", "wellness", "support", "other"];

export default function Partnerships() {
  const [partners, setPartners] = useState(null);
  const [benefits, setBenefits] = useState(null);
  const [bands, setBands] = useState([]);
  const [pOpen, setPOpen] = useState(false);
  const [bOpen, setBOpen] = useState(false);
  const [pForm, setPForm] = useState({ name: "", email: "" });
  const [bForm, setBForm] = useState({ band_id: "", title: "", detail: "", sponsor: "", category: "hydration", value: 0, action: "Show at Safe Point", cta: "show" });

  const load = () => {
    api.get("/admin/partners").then(({ data }) => setPartners(data));
    api.get("/admin/benefits").then(({ data }) => setBenefits(data));
  };
  useEffect(() => { load(); api.get("/admin/bands").then(({ data }) => setBands(data)); }, []);

  const createPartner = async () => {
    if (!pForm.name || !pForm.email) { toast.error("Name and email required"); return; }
    try {
      await api.post("/admin/partners", pForm);
      toast.success("Partner created (password: carnival123)");
      setPOpen(false); setPForm({ name: "", email: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create"); }
  };

  const createBenefit = async () => {
    if (!bForm.band_id || !bForm.title) { toast.error("Band and title required"); return; }
    try {
      await api.post("/admin/benefits", { ...bForm, value: Number(bForm.value) });
      toast.success("Benefit / campaign added");
      setBOpen(false); setBForm({ ...bForm, title: "", detail: "", sponsor: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create"); }
  };

  const delBenefit = async (b) => {
    if (!window.confirm(`Delete "${b.title}"?`)) return;
    await api.delete(`/admin/benefits/${b.id}`);
    toast.success("Benefit deleted");
    load();
  };

  if (!partners || !benefits) return <Spinner />;

  return (
    <Page>
      <h1 className="font-head font-extrabold text-3xl">Partnerships</h1>
      <p className="text-sm" style={{ color: "var(--cs-fg2)" }}>Manage brand partners and the safety benefits they fund</p>

      {/* Partners */}
      <div className="flex items-center justify-between mt-6">
        <h2 className="font-head font-bold text-lg flex items-center gap-2"><Handshake size={19} style={{ color: "var(--cs-magenta)" }} /> Brand Partners</h2>
        <button data-testid="admin-partner-add-toggle" onClick={() => setPOpen(!pOpen)} className="cs-tap rounded-xl px-3 py-2 text-xs font-semibold flex items-center gap-1.5" style={{ background: "var(--cs-card)", border: "1px solid var(--cs-border)" }}>
          <Plus size={15} /> Add Partner
        </button>
      </div>
      {pOpen && (
        <div className="cs-card p-4 mt-3 rise grid sm:grid-cols-3 gap-3">
          <input data-testid="admin-partner-name" value={pForm.name} onChange={(e) => setPForm({ ...pForm, name: e.target.value })} placeholder="Partner name" className="ad-input" />
          <input data-testid="admin-partner-email" value={pForm.email} onChange={(e) => setPForm({ ...pForm, email: e.target.value })} placeholder="Login email" className="ad-input" />
          <button data-testid="admin-partner-save" onClick={createPartner} className="cs-tap magenta-grad rounded-xl text-white font-bold text-sm" style={{ height: 46 }}>Create Partner</button>
        </div>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
        {partners.map((p) => (
          <div key={p.id} data-testid={`admin-partner-${p.id}`} className="cs-card p-4">
            <p className="font-semibold">{p.name}</p>
            <p className="text-xs" style={{ color: "var(--cs-fg2)" }}>{p.email}</p>
          </div>
        ))}
      </div>

      {/* Benefits */}
      <div className="flex items-center justify-between mt-8">
        <h2 className="font-head font-bold text-lg flex items-center gap-2"><Gift size={19} style={{ color: "var(--cs-magenta)" }} /> Benefits & Campaigns</h2>
        <button data-testid="admin-benefit-add-toggle" onClick={() => setBOpen(!bOpen)} className="cs-tap rounded-xl px-3 py-2 text-xs font-semibold flex items-center gap-1.5" style={{ background: "var(--cs-card)", border: "1px solid var(--cs-border)" }}>
          <Plus size={15} /> Add Benefit
        </button>
      </div>
      {bOpen && (
        <div className="cs-card p-4 mt-3 rise grid sm:grid-cols-2 gap-3">
          <select data-testid="admin-benefit-band" value={bForm.band_id} onChange={(e) => setBForm({ ...bForm, band_id: e.target.value })} className="ad-input">
            <option value="">Select band…</option>
            {bands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <select data-testid="admin-benefit-category" value={bForm.category} onChange={(e) => setBForm({ ...bForm, category: e.target.value })} className="ad-input">
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input data-testid="admin-benefit-title" value={bForm.title} onChange={(e) => setBForm({ ...bForm, title: e.target.value })} placeholder="Benefit title" className="ad-input" />
          <input data-testid="admin-benefit-sponsor" value={bForm.sponsor} onChange={(e) => setBForm({ ...bForm, sponsor: e.target.value })} placeholder="Sponsor name" className="ad-input" />
          <input data-testid="admin-benefit-detail" value={bForm.detail} onChange={(e) => setBForm({ ...bForm, detail: e.target.value })} placeholder="Short description" className="ad-input sm:col-span-2" />
          <input data-testid="admin-benefit-value" type="number" value={bForm.value} onChange={(e) => setBForm({ ...bForm, value: e.target.value })} placeholder="Value ($)" className="ad-input" />
          <button data-testid="admin-benefit-save" onClick={createBenefit} className="cs-tap magenta-grad rounded-xl text-white font-bold text-sm" style={{ height: 46 }}>Add Benefit</button>
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-3 mt-3">
        {benefits.map((b) => (
          <div key={b.id} data-testid={`admin-benefit-${b.id}`} className="cs-card p-4 flex items-start justify-between">
            <div>
              <p className="font-semibold">{b.title} <span className="text-xs" style={{ color: "var(--cs-fg2)" }}>· {b.category}</span></p>
              <p className="text-xs" style={{ color: "var(--cs-fg2)" }}>{b.detail}</p>
              <p className="text-[11px] mt-1" style={{ color: "#52525b" }}>{b.band_name || "No band"} · {b.sponsor || "—"} · {b.redeemed} redeemed</p>
            </div>
            <button data-testid={`admin-benefit-delete-${b.id}`} onClick={() => delBenefit(b)} className="cs-tap rounded-lg p-2" style={{ background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.3)" }}>
              <Trash2 size={15} style={{ color: "var(--cs-red)" }} />
            </button>
          </div>
        ))}
      </div>

      <style>{`.ad-input{height:46px;border-radius:.65rem;padding:0 .9rem;background:var(--cs-card-hi);border:1px solid var(--cs-border);color:#fff;outline:none;font-size:15px}.ad-input:focus{border-color:var(--cs-magenta)}`}</style>
    </Page>
  );
}
